import { DatePipe } from '@angular/common';
import { FormControl } from '@angular/forms';
import { SortEvent, TreeNode } from 'primeng/api';
import { CategorySummaryDTO, PageableFilter } from 'src/app/admin-api';
import { FormUtil } from 'src/app/utils/form.util';
import { TableColumn } from './models/tableColumn';

export class TableUtil {
  static isRouterLinkAndValid(col: TableColumn, obj: any): boolean {
    return (
      col.routerLink !== undefined &&
      col.routerLink !== null &&
      (typeof col.routerLink === 'string' || col.routerLink(obj)?.trim()) &&
      col.linkActive(obj) &&
      (!col.routerLinkFieldName ||
        (this.objValue(col, obj, col.routerLinkFieldName) !== null &&
          this.objValue(col, obj, col.routerLinkFieldName) !== undefined))
    );
  }

  static routerLink(col: TableColumn, obj: any): string {
    if (
      this.isRouterLinkAndValid(col, obj) &&
      typeof col.routerLink === 'string'
    )
      return col.routerLink;
    if (
      this.isRouterLinkAndValid(col, obj) &&
      typeof col.routerLink === 'function'
    )
      return col.routerLink(obj);
    return null;
  }

  static fullLink(col: TableColumn, obj: any): string {
    if (!this.isRouterLinkAndValid(col, obj)) return null;
    return `${
      typeof col.routerLink === 'function'
        ? col.routerLink(obj)
        : col.routerLink
    }${
      col.routerLinkFieldName
        ? this.objValue(col, obj, col.routerLinkFieldName)
        : ''
    }`;
  }

  static valueFromFilter(filter: PageableFilter): any | any[] {
    switch (filter.condition) {
      case 'in':
      case 'between':
        if (!Array.isArray(filter.value))
          return filter.value
            ?.trim()
            .split(',')
            .map((v) => v.trim());
    }
    return filter.value;
  }

  static objValue(col: TableColumn, obj: any, field?: string): any {
    const value = this.deepGet(
      obj,
      field?.trim()?.split('.') || col.field.trim().split('.')
    );
    if (typeof value === 'function') return value.call(obj);
    return value;
  }

  static deepGet(obj, keys: Array<string>) {
    return keys.reduce((xs, x) => xs?.[x] ?? null, obj);
  }

  static deepSet(obj: any, key: string, value: any): any {
    const keys = key.split('.');
    let level = { ...obj };
    const levels = [level];
    for (let index = 0; index < keys.length; index++) {
      const k = keys.at(index);
      if (Object.keys(level[k])?.length) {
        level = level[k];
        levels.push(level);
      } else {
        levels[levels.length - 1][k] = value;
      }
    }
    level = null;
    for (let index = levels.length - 1; index >= 0; index--) {
      if (level) {
        const clone = { ...level };
        level = { ...levels[index] };
        level[keys[index]] = clone;
      } else level = levels[index];
    }

    return level;
  }

  static externalLink(link: string) {
    return link.includes('http://') || link.includes('https://');
  }

  static objDisplay(col: TableColumn, obj: any): any {
    if (col?.displayFunction) return col.displayFunction(obj);
    const value = this.objValue(col, obj);
    if (Array.isArray(value)) return value.toString().replaceAll(',', ', ');
    return value;
  }

  static dateValue(control: FormControl): Array<Date | null> {
    if (control.value && control.value instanceof Date) {
      const date = control.value as Date;
      let nextDay = new Date(date.getTime());
      nextDay = new Date(nextDay.setDate(nextDay.getDate() + 1));
      return [date, nextDay];
    } else if (control.value && control.value instanceof Array) {
      return control.value;
    }
    return [null, null];
  }

  static timeDiffLabel(dateStart: string | Date, datePipe: DatePipe): string {
    if (!dateStart) return null;
    if (typeof dateStart === 'string') dateStart = FormUtil.utcDate(dateStart);
    return FormUtil.timeDiffLabel(dateStart, datePipe);
  }

  static timeLabel(
    dateStart: string | Date,
    datePipe: DatePipe
  ): string | null {
    if (!dateStart) return null;
    if (typeof dateStart === 'string') dateStart = FormUtil.utcDate(dateStart);
    return datePipe.transform(dateStart, 'dd/MM/yyyy HH:mm:ss');
  }

  static tableSort($event: SortEvent): void {
    $event.data?.sort((data1, data2) => {
      const value1 = TableUtil.deepGet(data1, $event.field.trim().split('.'));
      const value2 = TableUtil.deepGet(data2, $event.field.trim().split('.'));
      let result = null;

      if (value1 == null && value2 != null) result = -1;
      else if (value1 != null && value2 == null) result = 1;
      else if (value1 == null && value2 == null) result = 0;
      else if (typeof value1 === 'string' && typeof value2 === 'string')
        result = FormUtil.semAcento(value1.trim()).localeCompare(
          FormUtil.semAcento(value2.trim())
        );
      else result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0;

      return -1 * ($event.order || 1) * result;
    });
  }

  static categoryToNode(
    category: CategorySummaryDTO
  ): TreeNode<CategorySummaryDTO> {
    const group = {
      data: category,
      droppable: false,
      key: category.categoryId.toString(),
      type: 'attributeGroup',
      expanded: false,
      children: category.subcategoriesList?.map((a) => this.categoryToNode(a))
    };
    group.children?.sort((c1, c2) => c1.data.externalId - c2.data.externalId);
    group.data.subcategories = group.children?.reduce(
      (sum, c) => (sum += c.data.subcategories || 0),
      group.children?.length || 0
    );
    group.data.products = group.children?.reduce(
      (sum, c) => (sum += c.data.products || 0),
      group.data.products || 0
    );
    return group;
  }
}
